import './styles/leaderboardDialog.css';
import Dialog from '../../subcomponents/Dialog';
import Table from '../../subcomponents/Table';
import { useEffect, useState } from 'react';
import Dropdown from '../../subcomponents/Dropdown';
import { fetchAllLeaderboards } from '../../utils/leaderboardHelper';

type LeaderboardDialogProps = {
  open: boolean;
  onClose: () => void;
};

type LeaderboardEntry = {
  name: string;
  score: number;
  words: number;
  rank: number;
  isUser: boolean;
};

type LeaderboardData = {
  gameMode: string;
  daily: LeaderboardEntry[];
  allTime: LeaderboardEntry[];
};

function LeaderboardDialog(props: LeaderboardDialogProps) {
  const { open, onClose } = props;

  const gameModeOptions = [
    { value: 'blitzSimple', label: '4x4 Blitz' },
    { value: 'blitzDeluxe', label: '5x5 Blitz' },
    { value: 'endlessSimple', label: '4x4 Endless' },
    { value: 'endlessDeluxe', label: '5x5 Endless' },
  ];

  const timeFrameOptions = [
    { value: 'today', label: 'Today' },
    { value: 'all_time', label: 'All Time' },
  ];

  const [selectedMode, setSelectedMode] = useState<string>('blitzSimple');
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<string>('today');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const [initialDataFetch, setInitialDataFetch] = useState<boolean>(true);

  useEffect(() => {
    if (open && initialDataFetch) {
      const loadLeaderboards = async () => {
        try {
          setLoading(true);
          setHasError(false);
          const results = await fetchAllLeaderboards();
          setLeaderboardData(results);
          setTimeout(() => {
            setInitialDataFetch(true);
          }, 15000);
        } catch (err) {
          console.error('Leaderboard fetch failed:', err);
          setHasError(true);
        } finally {
          setLoading(false);
          setInitialDataFetch(false);
        }
      };
      loadLeaderboards();
    }
  }, [open]);

  const selectedSet = leaderboardData.find((entry) => entry.gameMode === selectedMode);
  const isDaily = selectedTimeFrame === 'today';
  const relevantData = isDaily ? (selectedSet?.daily ?? []) : (selectedSet?.allTime ?? []);

  const headers = ['Rank', 'Player', 'Words', 'Score'];

  const top10 = relevantData.slice(0, 10);

  // For larger rank values when user is outside top 10
  const formatRankNumber = (n: number): string => {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toString();
  };

  // Always create 10 rows — pad with empty entries if needed
  const top10Rows = Array.from({ length: 10 }, (_, i) => {
    const entry = top10[i];
    const rankDisplay = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : (entry?.rank ?? i + 1);

    return {
      cells: entry
        ? [rankDisplay, entry.name, entry.words, entry.score]
        : [rankDisplay, '-', '-', '-'],
      isUser: entry?.isUser ?? false,
    };
  });

  const userExtraRow =
    relevantData.length > 10 && relevantData[10].isUser
      ? {
          cells: [
            formatRankNumber(relevantData[10].rank),
            relevantData[10].name,
            relevantData[10].words,
            relevantData[10].score,
          ],
          isUser: true,
        }
      : null;

  const rows = userExtraRow ? [...top10Rows, userExtraRow] : top10Rows;

  return (
    <Dialog open={open} onClose={onClose} title="LEADERBOARD">
      <div className="leaderboard-container">
        <div className="flex justify-center gap-4 mb-4">
          <Dropdown
            value={selectedMode}
            options={gameModeOptions}
            onChange={setSelectedMode}
            fixedSize
          />
          <Dropdown
            value={selectedTimeFrame}
            options={timeFrameOptions}
            onChange={setSelectedTimeFrame}
            fixedSize
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="loader" />
          </div>
        ) : hasError ? (
          <div className="leaderboard-error-text">Failed to load leaderboard...</div>
        ) : (
          <Table
            headers={headers}
            rows={rows}
            columnWidths={['20%', '40%', '20%', '20%']}
            smallText
            noLines
            useExtraResultBorder
          />
        )}
      </div>
    </Dialog>
  );
}

export default LeaderboardDialog;
